import { Field } from '@atlaskit/form';
import { AsyncCreatableSelect } from '@atlaskit/select';
import { G300 } from '@atlaskit/theme/colors';
import {
  SanitizedFormattedHtmlMessage,
  SanitizedFormattedHtmlMessageProps
} from '@atlassiansox/bxpkit-core';
import { useStargateClient } from '@atlassiansox/bxpkit-core';
import { catalog } from '@atlassiansox/metal-client';
import styled from '@emotion/styled';
import debounce from 'lodash.debounce';
import React, { useEffect, useState } from 'react';
import { metalClient } from '../../../../metrics';
import { useDevopsState } from '../../hook';
import { messages } from '../../messages';
import { buildDevopsProduct } from '../../utility';
import { FieldType } from '../model';
import { ErrorMessage, FieldWrapper } from '../styled';
import { IconValidation } from './icon-validation';
import { ValidationStateType } from '../../types';
import {
  useAppDispatch,
  useAppSelector,
  updateLoggedInFormValid,
  updateLoggedInFormDataProducts
} from '../../../../redux';
// import { useAnalyticsEvents } from '@atlaskit/analytics-next';

type OptionsType = {
  readonly value: string;
  readonly label: string;
};

function createOption(value: string) {
  return {
    label: value,
    value
  };
}

export function InputWorkspaceExisting() {
  // TODO: there should be some analytics but im not seeing any in the old form
  // besides on the switch betweeen the dropdown and input for sites

  const { intl, bbworkspaces } = useDevopsState();
  const dispatch = useAppDispatch();
  const bbWorkspaceName = useAppSelector(
    ({ loggedInFormData: { bbWorkspaceName } }) => bbWorkspaceName
  );
  const stargate = useStargateClient();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [options, setOptions] = useState<OptionsType[]>([]);
  const [newOption, setNewOption] = useState<OptionsType>();
  const [inputValue, setInputValue] = useState<OptionsType>();
  const [validationState, setValidationState] = useState<ValidationStateType>();
  const [validationMessage, setValidationMessage] = useState<
    undefined | SanitizedFormattedHtmlMessageProps
  >();

  const validSitename = /^[a-z0-9]+[a-z0-9-]+[a-z0-9]+$/;

  const workspaceMap = new Map(
    bbworkspaces?.map((obj) => [obj?.workspace?.name, obj])
  );

  useEffect(() => {
    setOptions(
      [...workspaceMap.keys()].map((workspaceName) => {
        return createOption(workspaceName);
      })
    );
  }, [bbworkspaces]);

  useEffect(() => {
    // no selection has been made, or a new site was just added to options
    if (!bbWorkspaceName) {
      setInputValue(options[0]);
      return;
    }

    // selection has been made, and may or may not be included in options
    if (bbWorkspaceName && options) {
      const targetOption = options.find((o) => o.value === bbWorkspaceName);
      if (targetOption) {
        // already included in options, existing site
        setNewOption(undefined);
        setInputValue(targetOption);
      } else {
        // not included in options, new site
        const createdOption = createOption(bbWorkspaceName);
        setNewOption(createdOption);
        setInputValue(createdOption);
      }
    }
  }, [options, bbWorkspaceName]);

  async function updateWorkspacesInDevopsState() {
    if (!inputValue) {
      dispatch(updateLoggedInFormValid(false));
      return;
    }

    const { value: bbWorkspaceName } = inputValue;

    const selectedWorkspace = workspaceMap.get(bbWorkspaceName);

    const breachStatus = false;
    // a workspace is selected from the dropdown, and it was an existing workspace
    // validate to check if the workspaces name can be used as a site name as well
    if (selectedWorkspace) {
      await stargate
        .checkCloudNameAvailability({
          cloudName: bbWorkspaceName
        })
        .then((res) => {
          if (res?.availability === 'AVAILABLE') {
            const { productsNew, productsExisting } = buildDevopsProduct({
              cloudName: bbWorkspaceName,
              breachStatus,
              existingWorkspace: true
            });
            dispatch(
              updateLoggedInFormDataProducts({
                cloudName: bbWorkspaceName,
                bbWorkspaceName,
                productsNew,
                productsExisting
              })
            );
          } else {
            const { productsNew, productsExisting } = buildDevopsProduct({
              cloudName: bbWorkspaceName,
              breachStatus,
              existingWorkspace: true
            });
            dispatch(
              updateLoggedInFormDataProducts({
                cloudName: undefined,
                bbWorkspaceName,
                productsNew,
                productsExisting
              })
            );
          }
          return res;
        })
        .catch(() => {
          metalClient.error.submit({
            name: catalog.error.REQUEST,
            page: 'sign-up-layout-devops'
          });
        });

      dispatch(updateLoggedInFormValid(true));
    } else if (validationState !== 'error') {
      // user typed a new workspace and it can be used as a site as well
      const { productsNew, productsExisting } = buildDevopsProduct({
        cloudName: bbWorkspaceName,
        breachStatus
      });
      dispatch(
        updateLoggedInFormDataProducts({
          cloudName: bbWorkspaceName,
          bbWorkspaceName,
          productsNew,
          productsExisting
        })
      );
      dispatch(updateLoggedInFormValid(true));
    }
  }
  // set input value in DevopsState whenever it changes
  // @ts-ignore
  useEffect(() => {
    void updateWorkspacesInDevopsState();
  }, [inputValue]);

  // user selects 'Create ...'
  function handleCreateOption(value: string) {
    const option = createOption(value);
    setNewOption(option);
    setInputValue(option);
  }

  // user types into input
  async function loadOptions(
    value: string,
    callback: (options: OptionsType[]) => void
  ) {
    setInputValue(undefined);
    const validState = await validateSitename(value);
    if (validState === 'error') {
      metalClient.error.submit({
        name: catalog.error.INPUT,
        page: 'sign-up-layout-devops',
        component: 'devops-input-workspace-existing'
      });
      setIsMenuOpen(false);
    } else {
      setIsMenuOpen(true);
    }
    setValidationState(validState);
    // filter out options that do not match what's currently typed,
    // otherwise the 'Create ...' prompt lands at the bottom of the list
    callback(
      options.filter((option) =>
        option.label.toLowerCase().includes(value.toLowerCase())
      )
    );
  }

  // user selects from the dropdown
  function handleChange(value: OptionsType) {
    setInputValue(value);
  }

  function handleBlur() {
    setValidationMessage(undefined);
    setValidationState(undefined);
  }

  function handleMenuOpen() {
    if (validationState !== 'error') {
      setIsMenuOpen(true);
    }
  }

  function handleMenuClose() {
    setIsMenuOpen(false);
  }

  function validateSitename(value: string): Promise<ValidationStateType> {
    return new Promise(async (resolve) => {
      setValidationMessage(undefined);
      if (value.length === 0) {
        resolve(undefined);
      }
      if (
        !validSitename.test(value) ||
        (value.length > 0 && value.length < 3)
      ) {
        setValidationMessage(messages.errorSitenameInvalid);
        resolve('error');
      }
      if (options.map((p) => p.value).includes(value)) {
        resolve('success');
      }

      setValidationState('validating');
      const siteNameCheck = await stargate.checkCloudNameAvailability({
        cloudName: value
      });

      if (siteNameCheck?.availability !== 'AVAILABLE') {
        setValidationMessage(messages.errorSitenameUnavailable);
        resolve('error');
      } else {
        resolve('success');
      }

      resolve(undefined);
    });
  }
  const handleInputChange = (value: string) => {
    if (value === '') {
      setValidationMessage(undefined);
      setValidationState(undefined);
    }
  };

  return (
    <>
      <Field
        name="sitenameExisting"
        label={intl.formatMessage(messages.inputLabelWorkspaceExisting)}
        isRequired
      >
        {({ fieldProps }: FieldType) => {
          return (
            <FieldWrapper>
              {validationMessage && (
                <ErrorMessage className="location-bottom">
                  <SanitizedFormattedHtmlMessage {...validationMessage} />
                </ErrorMessage>
              )}
              <div data-testid="workspaceExisting">
                <StyledAsyncCreatableSelect
                  {...fieldProps}
                  loadOptions={debounce(loadOptions, 1000)}
                  defaultOptions={newOption ? [newOption, ...options] : options}
                  allowCreateWhileLoading={false}
                  isClearable={false}
                  validationState={validationState}
                  placeholder={''}
                  onCreateOption={handleCreateOption}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onMenuOpen={handleMenuOpen}
                  onMenuClose={handleMenuClose}
                  menuIsOpen={isMenuOpen}
                  value={inputValue}
                  onInputChange={handleInputChange}
                  elemAfterInput={
                    <IconValidation validationState={validationState} />
                  }
                />
              </div>
            </FieldWrapper>
          );
        }}
      </Field>
    </>
  );
}

// for possible future use:
// const SitenameDescription = styled.div`
//   color: ${N500};
//   font-weight: 400;
//   font-size: 12px;
//   margin: 3px 0px;
// `;

const StyledAsyncCreatableSelect = styled(AsyncCreatableSelect)`
  font-size: 14px;
  > div {
    ${(props) => {
      if (props.validationState === 'success') {
        return ` border-color:${G300} !important`; // green
      }
      return '';
    }};
  }
  svg {
    margin-bottom: 0px;
  }
`;
